import React, { useMemo, useState } from "react";
import styles from "./payment-selection.module.scss";
import useWindowDimensions, { CurrentScreen } from "../../../../hooks/use-window-dimensions";
import { Button, ToggleButtonGroup, ToggleOption } from "../index";
import { WalletDisplay } from "../wallet-display";
import { toast } from "react-toastify";
import { PaymentOptions } from "./payment-method-selection.types";
import { BankIcon, ChevronRight, CreditCardIcon, MomoIcon, WalletPaymentIcon } from "../../../../assets/icons";
import { WalletDetail } from "../../wallet/models/CustomerWalletDetails";

export interface PaymentMethodSelectionProps {
	amount: number;
	currency?: string;
	onCancel?: () => void;
	onContinue: (paymentMethod: PaymentOptions) => void;
	availableMethods?: PaymentOptions[]; // Optional: restrict which methods to show
	disabledMethods?: PaymentOptions[]; // Optional: methods that should be disabled
}

export const PaymentMethodSelection: React.FC<PaymentMethodSelectionProps> = ({
	amount,
	currency = "NGN",
	onCancel,
	onContinue,
	availableMethods = [PaymentOptions.Card, PaymentOptions.Wallet, PaymentOptions.Bank, PaymentOptions.Mono],
	disabledMethods = [],
}) => {
	const currentScreen: CurrentScreen = useWindowDimensions();
	const [selectedMethod, setSelectedMethod] = useState<string>("");
	const [walletInfo, setWalletInfo] = useState<WalletDetail | null>(null);

	const handleWalletInfo = (info: WalletDetail) => {
		setWalletInfo(info);
	};

	const allPaymentOptions: ToggleOption[] = [
		{
			value: PaymentOptions.Card.toString(),
			label: "Card",
			leftIcon: <CreditCardIcon />,
			rightIcon: <ChevronRight />,
		},
		{
			value: PaymentOptions.Wallet.toString(),
			label: "Wallet",
			leftIcon: <WalletPaymentIcon />,
			rightIcon: <ChevronRight />,
			customContent: <WalletDisplay onWalletReady={handleWalletInfo} />,
			expandOnSelect: true,
		},
		{
			value: PaymentOptions.Bank.toString(),
			label: "Bank Transfer",
			leftIcon: <BankIcon />,
			rightIcon: <ChevronRight />,
		},
		{
			value: PaymentOptions.Mono.toString(),
			label: "Momo",
			leftIcon: <MomoIcon />,
			rightIcon: <ChevronRight />,
		},
		{
			value: PaymentOptions.Cash.toString(),
			label: "Cash",
			leftIcon: <CreditCardIcon />,
			rightIcon: <ChevronRight />,
		},
		// {
		// 	value: PaymentOptions.BorrowToken.toString(),
		// 	label: "Borrow Token",
		// 	leftIcon: <PlusIcon />,
		// 	rightIcon: <ChevronRight />,
		// },
	];

	// Only render options explicitly included in availableMethods.
	// BorrowToken is absent from the default, so it only surfaces when the
	// caller (e.g. BuyElectricityPage) explicitly adds it for eligible prepaid users.
	const visibleOptions = useMemo(
		() =>
			allPaymentOptions
				.filter(opt => availableMethods.includes(Number(opt.value) as PaymentOptions))
				.map(opt => ({
					...opt,
					isDisabled: disabledMethods.includes(Number(opt.value) as PaymentOptions),
				})),
		[availableMethods, disabledMethods]
	);

	const formatAmount = (value: number): string => {
		return value.toLocaleString("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	const handleContinue = () => {
		if (!selectedMethod) {
			toast.error("Please select a payment method");
			return;
		}

		const selectedPaymentOption = Number(selectedMethod) as PaymentOptions;

		// Check if selected method is disabled
		if (disabledMethods.includes(selectedPaymentOption)) {
			toast.error("This payment method is currently unavailable");
			return;
		}

		if (selectedMethod === PaymentOptions.Wallet.toString()) {
			if (!walletInfo) {
				toast.error("Unable to load wallet information. Please try again.");
				return;
			}

			const availableBalance = walletInfo.availableBalance ?? 0;
			if (amount > availableBalance) {
				const shortfall = amount - availableBalance;
				toast.error(`Insufficient balance. You need ${currency} ${shortfall.toLocaleString()} more. Please fund your wallet.`);
				return;
			}
		}

		if (
			selectedMethod === PaymentOptions.Bank.toString() ||
			selectedMethod === PaymentOptions.Mono.toString() ||
			selectedMethod === PaymentOptions.Cash.toString()
		) {
			toast.warning("Payment option not yet implemented");
			return;
		}

		onContinue(selectedPaymentOption);
	};

	return (
		<div className={styles.container}>
			{/* Header */}
			<div className={styles.header}>
				<h1 className={styles.title}>Choose Payment method</h1>
				<p className={styles.subtitle}>Select a preferred payment channel</p>
			</div>

			{/* Payment Methods */}
			<div className={styles.methodsContainer}>
				<ToggleButtonGroup
					options={visibleOptions}
					value={selectedMethod}
					onChange={(value: string) => {
						setSelectedMethod(value);
					}}
					name="payment-method"
					direction="vertical"
				/>
			</div>

			{/* Actions */}
			<div className={styles.actions}>
				{!currentScreen.isMobile && (
					<Button variant="ghost" size="small" onClick={onCancel} className={styles.cancelButton}>
						Cancel
					</Button>
				)}
				<Button
					variant="primary"
					size={currentScreen.isMobile ? "default" : "small"}
					disabled={selectedMethod === undefined}
					onClick={handleContinue}
					className={styles.continueButton}
					fullWidth={currentScreen.isMobile}
				>
					{selectedMethod === PaymentOptions.BorrowToken.toString() ? "Proceed" : `Pay - ${currency} ${formatAmount(amount)}`}
				</Button>
			</div>
		</div>
	);
};
